import Axios from 'api/client';
import qs from 'qs';
import { BundledTicket, CustomFieldRequestValue, FilterTicketType, Ticket } from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface FetchTicketsParams extends FilterParams {
    urlParams?: string;
}

interface UpdateTicketCustomField {
    [field: string]: CustomFieldRequestValue;
}

interface UpdateTicketCustomFieldParams {
    custom_fields: UpdateTicketCustomField;
}

interface FetchTicketBundlesParams extends FilterParams {}

interface FetchTicketTypesParams extends FilterParams {}

const fetchTickets = (accountId: number, params: FetchTicketsParams) => {
    const { scope = 'all', filters, ...restParams } = params;

    return Axios.post<PaginatedData<Ticket>>(`/accounts/${accountId}/get_tickets`, {
        ...filters,
        ...restParams,
        scope,
        include: 'bundle,event.campaign,latestCheckin.user',
    }).then(response => response.data);
};

const fetchTicketBundles = (accountId: number, params: FetchTicketTypesParams = {}) => {
    const { filters, ...restParams } = params;
    const stringifiedFilters = filters ? qs.stringify(filters) : '';

    return Axios.get<PaginatedData<BundledTicket>>(
        `/accounts/${accountId}/ticket-bundles?${stringifiedFilters}`,
        {
            params: restParams,
        },
    ).then(response => response.data);
};

const fetchTicketById = (
    ticketId: number,
    params: FetchTicketsParams = { include: 'bundle.tickets,latestCheckin.user' },
) => Axios.get<Ticket>(`/tickets/${ticketId}`, { params }).then(response => response.data);

const revokeTicket = (ticketId: number) =>
    Axios.post(`/tickets/${ticketId}/revoke`).then(response => response.data);

const checkInTicket = (ticketId: number) =>
    Axios.post(`/tickets/${ticketId}/check-in`).then(response => response.data);

const cancelCheckInTicket = (ticketId: number) =>
    Axios.post(`/tickets/${ticketId}/cancel-check-in`).then(response => response.data);

const resendTicketToEmail = (ticketId: number, params: { email: string }) =>
    Axios.post(`/tickets/${ticketId}/resend`, params).then(response => response.data);

const updateTicketCustomFields = (ticketId: number, customFields: UpdateTicketCustomFieldParams) =>
    Axios.patch(`/tickets/${ticketId}`, customFields).then(response => response.data);

const fetchTicketTypes = (accountId: number, params: FetchTicketTypesParams = {}) => {
    const { filters, ...restParams } = params;
    const stringifiedFilters = filters ? qs.stringify(filters) : '';

    return Axios.get<PaginatedData<FilterTicketType>>(
        `/accounts/${accountId}/ticket-types?${stringifiedFilters}`,
        {
            params: restParams,
        },
    ).then(response => response.data);
};

export {
    fetchTickets,
    fetchTicketById,
    fetchTicketBundles,
    fetchTicketTypes,
    revokeTicket,
    checkInTicket,
    cancelCheckInTicket,
    resendTicketToEmail,
    updateTicketCustomFields,
};
export type { FetchTicketTypesParams, FetchTicketBundlesParams };
